/**
 * Shared client-side metadata for the Airbnb-style category reviews
 * (web artifact). Mirrors `artifacts/api-server/src/lib/review-categories.ts`
 * — slugs MUST stay in sync with the server enum.
 */
import {
  Droplet,
  CheckCircle,
  MessageCircle,
  Key,
  Tag,
  Shield,
  BookOpen,
  type LucideIcon,
} from "lucide-react";

export type ReviewDirection = "guest_to_car" | "host_to_guest";

export interface CategoryMeta {
  slug: string;
  label: string;
  icon: LucideIcon;
}

export const GUEST_TO_CAR_CATEGORIES: CategoryMeta[] = [
  { slug: "cleanliness",   label: "Cleanliness",   icon: Droplet },
  { slug: "accuracy",      label: "Accuracy",      icon: CheckCircle },
  { slug: "communication", label: "Communication", icon: MessageCircle },
  { slug: "check_in",      label: "Check-in",      icon: Key },
  { slug: "value",         label: "Value",         icon: Tag },
  { slug: "condition",     label: "Condition",     icon: Shield },
];

export const HOST_TO_GUEST_CATEGORIES: CategoryMeta[] = [
  { slug: "cleanliness",     label: "Cleanliness",     icon: Droplet },
  { slug: "communication",   label: "Communication",   icon: MessageCircle },
  { slug: "vehicle_care",    label: "Vehicle care",    icon: Shield },
  { slug: "rules_adherence", label: "Rules adherence", icon: BookOpen },
];

export const GUEST_TO_CAR_TAGS = [
  { slug: "spotless",            label: "Spotless" },
  { slug: "easy_pickup",         label: "Easy pickup" },
  { slug: "drove_like_a_dream",  label: "Drove like a dream" },
  { slug: "great_for_photos",    label: "Great for photos" },
  { slug: "responsive_host",     label: "Responsive host" },
  { slug: "as_described",        label: "As described" },
  { slug: "smooth_handover",     label: "Smooth handover" },
  { slug: "great_value",         label: "Great value" },
];

export const HOST_TO_GUEST_TAGS = [
  { slug: "communicative",     label: "Communicative" },
  { slug: "careful_driver",    label: "Careful driver" },
  { slug: "on_time_return",    label: "On-time return" },
  { slug: "left_it_spotless",  label: "Left it spotless" },
  { slug: "respectful",        label: "Respectful" },
  { slug: "easy_to_work_with", label: "Easy to work with" },
];

export const MAX_TAGS_PER_REVIEW = 3;

export function getCategoriesFor(direction: ReviewDirection): CategoryMeta[] {
  return direction === "guest_to_car" ? GUEST_TO_CAR_CATEGORIES : HOST_TO_GUEST_CATEGORIES;
}

export function getTagsFor(direction: ReviewDirection) {
  return direction === "guest_to_car" ? GUEST_TO_CAR_TAGS : HOST_TO_GUEST_TAGS;
}

const ALL_CATEGORIES: Record<string, CategoryMeta> = Object.fromEntries(
  [...GUEST_TO_CAR_CATEGORIES, ...HOST_TO_GUEST_CATEGORIES].map((c) => [c.slug, c]),
);
const ALL_TAGS: Record<string, { slug: string; label: string }> = Object.fromEntries(
  [...GUEST_TO_CAR_TAGS, ...HOST_TO_GUEST_TAGS].map((t) => [t.slug, t]),
);

export function getCategoryMeta(slug: string): CategoryMeta | null {
  return ALL_CATEGORIES[slug] || null;
}

export function getTagLabel(slug: string): string {
  return ALL_TAGS[slug]?.label || slug.replace(/_/g, " ");
}
